import { FC, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { selectLocaleImprovementsEnabled, selectUserProjectId } from 'core/userContext';
import { useDataFeedsQuery } from 'modules/campaigns/lib/hooks';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { getUseSMSShorteningKey } from 'modules/templateEditor/EditSMS/context/SMSLinkShorteningContextProvider';
import { useTemplateTakeover } from 'modules/templateEditor/integrations/InStudioTemplateEditing/context';
import { useLocalesQueryContext } from 'modules/campaigns/lib/contexts/LocalesQueryContext';
import { useGetVerifiedContactCardById, useQueryTemplateAndContentById } from '../../hooks';
import { useTemplateState } from '../../hooks/useTemplateState';
import { SMSPrimaryContent, TemplateEditorState, TemplateQueryString } from '../../types';
import { useCampaignContext } from '../CampaignContext';
import { getQueryStringByKey } from '../utils';
import { TemplateContext, TemplateContextState } from './TemplateContext';

const TEMPLATE_QUERY_KEY = 'template';
const TEMPLATE_EDITOR_QUERY_KEY = 'templateEditor';

/**
 * The current template data form doesn't have value for Shorten and track. This value is updated/saved in local storage.
 * This function always gets the data from local storage for that specific project.
 * */
export const getIsSMSLinkShorteningEnabled = (projectId: number | string) => {
  return JSON.parse(`${window.localStorage.getItem(getUseSMSShorteningKey(projectId))}`);
};

const getTemplateEditorQueryString = (search?: string) => {
  return !!getQueryStringByKey(TEMPLATE_EDITOR_QUERY_KEY, search);
};

const addTemplateEditorQueryString = () => {
  if (!getTemplateEditorQueryString()) {
    const url = new URL(window.location.href);
    url.searchParams.set(TEMPLATE_EDITOR_QUERY_KEY, TemplateEditorState.Open);
    window.history.replaceState({}, '', url.toString());
  }
};

const removeTemplateEditorQueryString = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete(TEMPLATE_EDITOR_QUERY_KEY);
  window.history.replaceState({}, '', url.toString());
};

const getLocalesDisplayText = (
  defaultLocale: string,
  templateLocales: Record<'tag', string>[] = [],
) => {
  if (!defaultLocale && !(templateLocales?.length >= 1)) return [];

  const defaultLocaleText = `${defaultLocale} (default)`;

  return templateLocales.reduce(
    (prev, curr) => {
      if (curr.tag !== defaultLocale) {
        return [...prev, curr.tag];
      } else {
        return prev;
      }
    },
    [defaultLocaleText],
  );
};

export const TemplateProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const projectId = useSelector(selectUserProjectId);
  const isLocaleImprovementsEnabled = useSelector(selectLocaleImprovementsEnabled);
  const location = useLocation();
  const takeover = useTemplateTakeover();
  const [isSMSLinkShorteningEnabled, setIsSMSLinkShorteningEnabled] = useState<boolean>(
    getIsSMSLinkShorteningEnabled(projectId),
  );

  useEffect(() => {
    if (takeover.isOpen) {
      addTemplateEditorQueryString();
    } else {
      removeTemplateEditorQueryString();
    }
  }, [takeover.isOpen]);

  const { campaign } = useCampaignContext();

  const { locale } = useQueryParamContext();

  const dataFeeds = useDataFeedsQuery();

  const {
    templateDefaultLocale,
    activeTemplateLocales,
    refetch: refetchLocaleQuery,
    isLoading: localesQueryLoading,
  } = useLocalesQueryContext();

  const defaultLocale = templateDefaultLocale?.tag;

  const { templateId = 0, messageMedium } = campaign;

  useEffect(() => {
    const template = getQueryStringByKey(TEMPLATE_QUERY_KEY, location?.search);
    const hasTemplateEditor = getTemplateEditorQueryString(location?.search);

    if (template === TemplateQueryString.Imported.toLocaleLowerCase() || hasTemplateEditor) {
      takeover.open(takeover.viewOptions.settings);
    }

    /*
      whenever we have a template query string when we load the application, we want to remove it after we have
      set the proper state for the campaign editor.  This allow the user to share the url to the campaign without
      having some random behavior for the editor.
     */

    const url = new URL(window.location.href);
    url.searchParams.delete(TEMPLATE_QUERY_KEY);
    window.history.replaceState({}, '', url.toString());
  }, []);

  const currentLocale = locale || defaultLocale;

  const queryOptions = useMemo(() => {
    if (isLocaleImprovementsEnabled) {
      return {
        variables: {
          id: templateId,
          locale: defaultLocale ? { name: currentLocale } : undefined,
        },
        skip: templateId === 0 || localesQueryLoading,
        fetchPolicy: 'no-cache',
      };
    }

    return {
      variables: { id: templateId },
      skip: templateId === 0,
      fetchPolicy: 'no-cache',
    };
  }, [templateId, currentLocale, defaultLocale, localesQueryLoading]);

  const {
    results: templateAndContent,
    errorMessage = '',
    hasError,
    isLoading: isTemplateLoading,
    refetch,
  } = useQueryTemplateAndContentById(queryOptions);

  const { templateState } = useTemplateState(messageMedium);

  const primaryContent = templateAndContent?.primaryContent as SMSPrimaryContent;
  const contactId = primaryContent?.verifiedContactCardId;

  const contactCardQueryOptions = useMemo(
    () => ({
      skip: isTemplateLoading || hasError || !contactId,
      variables: {
        id: contactId,
      },
    }),
    [isTemplateLoading, hasError, contactId],
  );

  const { results: contactCardName } = useGetVerifiedContactCardById(contactCardQueryOptions);

  const refetchTemplate = () => {
    refetch();
    refetchLocaleQuery();
  };
  const value: TemplateContextState = {
    templateState,
    errorMessage,
    hasError,
    isLoading: isTemplateLoading || localesQueryLoading,
    templateAndContent,
    dataFeeds,
    refetch: refetchTemplate,
    contactCardName,
    templateLocales: getLocalesDisplayText(defaultLocale, activeTemplateLocales),
    isSMSLinkShorteningEnabled,
    setIsSMSLinkShorteningEnabled,
  };

  return <TemplateContext.Provider value={value}>{children}</TemplateContext.Provider>;
};
